/**
 * Shared leave-type helpers for Attendance (WFH naming varies across API/UI).
 */
import moment from 'moment'

export function isWFHLeaveType(type) {
  if (type == null || type === '') return false
  const t = String(type).trim().toLowerCase()
  if (!t) return false
  return (
    t === 'wfh' ||
    t.includes('work from home') ||
    t.includes('work-from-home') ||
    t.startsWith('wfh ') ||
    t.startsWith('wfh(') ||
    (t.includes('(partial)') && t.includes('wfh'))
  )
}

/** Present or half-day leave types require punch times when regularizing. */
export function isPresentOrHalfDayLeaveType(leaveType, leaveTypesFromDB = null) {
  if (!leaveType) return false

  const leaveTypeUpper = String(leaveType).toUpperCase().trim()

  if (
    leaveTypeUpper === 'PRESENT' ||
    leaveTypeUpper === 'P' ||
    leaveTypeUpper.includes('PRESENT')
  ) {
    return true
  }

  if (
    leaveTypeUpper === '1ST HALF DAY' ||
    leaveTypeUpper === '2ND HALF DAY' ||
    leaveTypeUpper === 'HALF DAY' ||
    leaveTypeUpper === '1ST HALF' ||
    leaveTypeUpper === '2ND HALF' ||
    leaveTypeUpper.includes('HALF DAY') ||
    leaveTypeUpper.includes('HALF')
  ) {
    return true
  }

  if (leaveTypesFromDB && leaveTypesFromDB.length > 0) {
    const foundType = leaveTypesFromDB.find(lt =>
      lt.code === leaveType ||
      lt.display_name === leaveType ||
      (lt.code && lt.code.toUpperCase() === leaveTypeUpper) ||
      (lt.display_name && lt.display_name.toUpperCase() === leaveTypeUpper),
    )
    if (foundType) {
      const codeUpper = (foundType.code || '').toUpperCase()
      const nameUpper = (foundType.display_name || '').toUpperCase()
      if (
        codeUpper === 'PRESENT' || codeUpper === 'P' ||
        nameUpper === 'PRESENT' ||
        nameUpper.includes('PRESENT') ||
        nameUpper.includes('HALF')
      ) {
        return true
      }
    }
  }

  return false
}

/**
 * Whether punch in/out times are required when regularizing attendance.
 * - No leave type: required (missing punch regularization).
 * - Present / half-day: required.
 * - WFH, Sick, Casual, etc.: not required.
 */
export function requiresRegularizationTime(leaveType, leaveTypesFromDB = null) {
  if (leaveType == null || leaveType === '') return true
  if (isPresentOrHalfDayLeaveType(leaveType, leaveTypesFromDB)) return true
  return false
}

export function isWFHLeaveRecord(leave) {
  if (!leave) return false
  if (leave.partial_wfh === true) return true
  return isWFHLeaveType(
    leave.leave_type || leave.type || leave.issue_type
  )
}

/** Resolve employee id from a team/org member row (field names vary by API). */
export function resolveMemberEmpId(member) {
  if (!member) return null
  const id = member.emp_id ?? member.id ?? member.employee_id ?? member.empId
  return id == null || id === '' ? null : id
}

/** Resolve employee id from a leave/WFH record. */
export function resolveLeaveEmpId(leave) {
  if (!leave) return null
  const id = leave.emp_id ?? leave.employee_id ?? leave.empId
  return id == null || id === '' ? null : id
}

/**
 * Approved (or manager/OH-approved awaiting HR) leave/WFH.
 * WFH Today KPI counts only these — pending WFH requests are excluded.
 */
export function isApprovedLeaveStatus(status) {
  const s = String(status || '').trim().toLowerCase()
  if (!s || s.includes('reject') || s.includes('cancel')) return false
  if (s === 'approved') return true
  // Manager / Operation Head approved, still pending a later stage
  if (s.includes('approved') && s.includes('pending')) return true
  return false
}

/**
 * Parse leave date to local start-of-day.
 * Strict regional formats are tried before loose parsing to avoid DD/MM vs MM/DD ambiguity.
 */
export function parseAttendanceLeaveDay(value) {
  if (value == null || value === '') return null
  if (moment.isMoment(value)) return value.clone().startOf('day')

  const strict = moment(
    value,
    ['YYYY-MM-DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'MM/DD/YYYY', moment.ISO_8601],
    true
  )
  if (strict.isValid()) return strict.startOf('day')

  const loose = moment(value)
  return loose.isValid() ? loose.startOf('day') : null
}

/** Inclusive from/to bounds for a leave/WFH record (handles single-day `date` fields). */
export function getLeaveRecordBounds(leave, parseDay = parseAttendanceLeaveDay) {
  if (!leave) return { from: null, to: null }
  const fromRaw = leave.from_date ?? leave.date ?? leave.datestring ?? leave.start_date
  const toRaw = leave.to_date ?? leave.end_date ?? fromRaw
  return {
    from: parseDay(fromRaw),
    to: parseDay(toRaw),
  }
}

/** True when `day` falls within the leave/WFH record range (inclusive). */
export function leaveCoversDay(leave, day, parseDay = parseAttendanceLeaveDay) {
  const { from, to } = getLeaveRecordBounds(leave, parseDay)
  if (!from || !to) return false
  const d = moment.isMoment(day) ? day.clone().startOf('day') : parseDay(day)
  if (!d) return false
  return d.isSameOrAfter(from, 'day') && d.isSameOrBefore(to, 'day')
}

/**
 * Count distinct employees on approved WFH today from loaded leave records.
 */
export function countApprovedWfhToday(leaves, { memberIdSet, today, parseDay = parseAttendanceLeaveDay }) {
  if (!Array.isArray(leaves) || leaves.length === 0) return 0
  const wfhIds = new Set()
  const scopeActive = memberIdSet && memberIdSet.size > 0

  leaves.forEach(leave => {
    if (!leave || !isWFHLeaveRecord(leave)) return
    if (!isApprovedLeaveStatus(leave.status)) return
    if (!leaveCoversDay(leave, today, parseDay)) return
    const empId = resolveLeaveEmpId(leave)
    if (!empId) return
    if (scopeActive && !memberIdSet.has(String(empId))) return
    wfhIds.add(String(empId))
  })

  return wfhIds.size
}
